import Checkbox from 'app/components/checkbox'
import FullPageDialog from 'app/components/full-page-dialog'
import TextInput from 'app/components/text-input'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import React, { ChangeEvent, MouseEvent, useContext, useEffect, useState } from 'react'
import { MitreData } from 'app/modules/shared/types'
import { assetsContext } from 'app/modules/shared'
import { statService } from 'app/modules/reports/services'
import { logger } from 'app/components'
import { FilterValues } from './filter-values'

type TechniqueType = {
  id: string
  parentId: string
  name: string
  subtechniques?: TechniqueType[]
}

type TacticType = {
  tactic: string
  name: string
  techniques: TechniqueType[]
}

const MitreSearch = ({
  onApply,
  searchTechniques,
  filterId,
  value,
}: {
  onApply: (indicators: string[], ...args: any[]) => void
  searchTechniques: string[]
  filterId?: string
  value?: string
}) => {
  const [isOpen, setOpen] = useState(false)
  const [allMitreData, setAllMitreData] = useState<TacticType[]>([])
  const [filteredMitreData, setFilteredMitreData] = useState<TacticType[]>([])
  const [selectedFinalTechniques, setSelectedFinalTechniques] = useState<string[]>(searchTechniques)
  const [selectedTechniques, setSelectedTechniques] = useState<TechniqueType[]>([])
  const [selectedSubTechniques, setSelectedSubTechniques] = useState<TechniqueType[]>([])
  const [allSelected, setAllSelected] = useState({
    tactics: [] as string[],
    techniques: [] as string[],
  })
  const [partiallySelected, setPartiallySelected] = useState({
    tactics: [] as string[],
    techniques: [] as string[],
  })
  const [searchInput, setSearchInput] = useState('')
  const [openTactic, setOpenTactic] = useState<TacticType>()
  const [openTechnique, setOpenTechnique] = useState<TechniqueType>()
  const [triggerSubmit, setTriggerSubmit] = useState(false)
  const [enableSubmit, setEnableSubmit] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [hasError, setHasError] = useState(false)
  const filterValues = value ? value.split(',') : []

  const { _t } = useTypedTranslation()
  const assets = useContext(assetsContext)

  const getSubtechniqueById = (id: string, filtered = false): TechniqueType | undefined => {
    const parent = getTechniqueById(id.split('.')[0], filtered)
    return parent?.subtechniques?.find((item) => item.id === id)
  }

  const getTechniqueById = (id: string, filtered = false): TechniqueType | undefined => {
    const data = filtered ? filteredMitreData : allMitreData
    for (const tactic of data) {
      for (const technique of tactic.techniques) {
        if (technique.id === id) {
          return technique
        }
      }
    }

    return undefined
  }

  const getTacticById = (id: string, filtered = false): TacticType | undefined => {
    const data = filtered ? filteredMitreData : allMitreData
    return data.find((tactic) => tactic.tactic === id)
  }

  const setSelectionFromInput = async (ids: string[], idToFilter?: string) => {
    const subtechniques = [] as TechniqueType[]
    const techniques = [] as TechniqueType[]

    for (const id of ids) {
      if (id === idToFilter) {
        // This is required because state update needs time
        setSelectedFinalTechniques(selectedFinalTechniques.filter((item) => item !== id))
        continue
      }

      const isSubtechnique = id.split('.').length > 1
      if (isSubtechnique) {
        const subtechnique = getSubtechniqueById(id, true)
        typeof subtechnique !== 'undefined' && subtechniques.push(subtechnique)
      } else {
        const technique = getTechniqueById(id, true)

        if (technique?.subtechniques && (technique?.subtechniques?.length ?? 0) > 0) {
          subtechniques.push(...technique.subtechniques)
          continue
        } else if (typeof technique !== 'undefined') {
          techniques.push(technique)
        }
      }
    }

    setSelectedSubTechniques([...subtechniques])
    setSelectedTechniques([...techniques])
  }

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget as HTMLInputElement
    setSearchInput(value)
  }

  const handleOpenTactic = (tactic: TacticType) => {
    setOpenTactic(tactic.tactic === openTactic?.tactic ? undefined : tactic)
    setOpenTechnique(undefined)
  }

  const handleOpenTechnic = (technique: TechniqueType) =>
    setOpenTechnique(technique.id === openTechnique?.id ? undefined : technique)

  const handleSelectSubtechnique = (subtechnique?: TechniqueType) => {
    if (!subtechnique) {
      return
    }

    const notSelectedNow = selectedSubTechniques.some((item) => item.id === subtechnique.id) //in the selceted list yet

    setSelectedSubTechniques(
      notSelectedNow
        ? selectedSubTechniques.filter((item) => item.id !== subtechnique.id)
        : [...selectedSubTechniques, subtechnique]
    )
  }

  const handleSelectTechnique = (technique?: TechniqueType) => {
    if (!technique) {
      return
    }

    const notSelectedNow =
      selectedTechniques.some((item) => item.id === technique.id) || //in the selceted list yet
      allSelected.techniques.includes(technique.id) ||
      partiallySelected.techniques.includes(technique.id)

    if (technique.subtechniques?.length) {
      setSelectedSubTechniques(
        notSelectedNow
          ? selectedSubTechniques.filter((sub) => !technique.subtechniques?.map((item) => item.id).includes(sub.id))
          : [...selectedSubTechniques, ...technique.subtechniques]
      )
    } else {
      setSelectedTechniques(
        notSelectedNow
          ? selectedTechniques.filter((item) => item.id !== technique.id)
          : [...selectedTechniques, technique]
      )
    }
  }

  const handleSelectTactic = (tactic?: TacticType, forcedValue?: boolean) => {
    const [newSelectedTechniques, newSelectedSubTechniques] = updateTacticSelection(tactic, forcedValue)
    setSelectedTechniques(newSelectedTechniques)
    setSelectedSubTechniques(newSelectedSubTechniques)
  }

  const handleSelectAllTactics = () => {
    const notSelectedNow = selectedFinalTechniques.length > 0
    if (notSelectedNow) {
      return handleClearSelection()
    }

    const newSelectedTechniques = [] as TechniqueType[]
    const newSelectedSubTechniques = [] as TechniqueType[]

    for (const tactic of filteredMitreData) {
      const [techniques, subtechniques] = updateTacticSelection(tactic, notSelectedNow)
      newSelectedTechniques.push(...techniques)
      newSelectedSubTechniques.push(...subtechniques)
    }

    setSelectedTechniques(newSelectedTechniques)
    setSelectedSubTechniques(newSelectedSubTechniques)
  }

  const updateTacticSelection = (tactic?: TacticType, forcedValue?: boolean): TechniqueType[][] => {
    if (!tactic) {
      return [[], []]
    }

    const notSelectedNow =
      forcedValue ?? (allSelected.tactics.includes(tactic.tactic) || partiallySelected.tactics.includes(tactic.tactic)) //in the selceted list yet

    let newSelectedTechniques = [...selectedTechniques]
    let newSelectedSubTechniques = [...selectedSubTechniques]

    for (const technique of tactic.techniques) {
      if (technique.subtechniques?.length) {
        if (notSelectedNow) {
          newSelectedSubTechniques = newSelectedSubTechniques.filter(
            (sub) => !technique.subtechniques?.map((item) => item.id).includes(sub.id)
          )
        } else {
          newSelectedSubTechniques = [...newSelectedSubTechniques, ...technique.subtechniques]
        }
      } else if (notSelectedNow) {
        newSelectedTechniques = newSelectedTechniques.filter((item) => item.id !== technique.id)
      } else {
        newSelectedTechniques = [...newSelectedTechniques, technique]
      }
    }

    return [newSelectedTechniques, newSelectedSubTechniques]
  }

  const handleClearSelection = () => {
    setSelectedTechniques([])
    setSelectedSubTechniques([])
    setAllSelected({ tactics: [], techniques: [] })
    setPartiallySelected({ tactics: [], techniques: [] })
    // Enable to save cleared filter
    setEnableSubmit(!!searchTechniques.length)
  }

  const handleRemoveTechnique = (id: string) => {
    const isSubtechnique = id.split('.').length > 1

    if (isSubtechnique) {
      return handleSelectSubtechnique(getSubtechniqueById(id))
    }

    handleSelectTechnique(getTechniqueById(id))
  }

  const handleRemoveFromInput = (id: string, e: MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setSelectionFromInput(selectedFinalTechniques, id)
    setTriggerSubmit(true)
  }

  const handleSubmit = () => {
    onApply(selectedFinalTechniques)
  }

  useEffect(() => {
    if (!searchInput) {
      setFilteredMitreData(allMitreData)
    }

    setFilteredMitreData(getFilteredMitreData(searchInput, allMitreData))
  }, [searchInput, allMitreData])

  useEffect(() => {
    if (openTactic) {
      setOpenTactic(getTacticById(openTactic.tactic, true))
    }

    if (openTechnique) {
      setOpenTechnique(getTechniqueById(openTechnique.id, true))
    }
  }, [filteredMitreData])

  useEffect(() => {
    // This effect is responsible to set the selected array and the checkboxes correctly
    // If all subtechniques of a certain technique is selected, we select the technique

    const finalArray = [] as string[]
    const allSelectedTactics = [] as string[]
    const allSelectedTechniques = [] as string[]
    const partiallySelectedTactics = [] as string[]
    const partiallySelectedTechniques = [] as string[]

    // Add subtechniques and techniques with all subtechniques selected
    // Add partially selected techniques
    for (const subtechnique of selectedSubTechniques) {
      if (allSelectedTechniques.includes(subtechnique.parentId)) {
        continue
      }

      const selectedParent = getTechniqueById(subtechnique.parentId)!

      if (
        selectedParent?.subtechniques?.every((item) => selectedSubTechniques.map((sub) => sub.id).includes(item.id))
      ) {
        finalArray.push(subtechnique.parentId)
        allSelectedTechniques.push(subtechnique.parentId)
        continue
      }

      finalArray.push(subtechnique.id)
      partiallySelectedTechniques.push(selectedParent.id)
    }

    // Add techniques with no subtechniques
    finalArray.push(...selectedTechniques.map((item) => item.id))

    // Get state of tactic checkboxes
    for (const tactic of allMitreData) {
      if (
        tactic.techniques.every(
          (item) =>
            allSelectedTechniques.includes(item.id) || selectedTechniques.some((technique) => technique.id === item.id)
        )
      ) {
        allSelectedTactics.push(tactic.tactic)
      } else if (
        tactic.techniques.some(
          (item) =>
            allSelectedTechniques.includes(item.id) ||
            partiallySelectedTechniques.includes(item.id) ||
            selectedTechniques.some((technique) => technique.id === item.id)
        )
      ) {
        partiallySelectedTactics.push(tactic.tactic)
      }
    }

    // Update states
    setSelectedFinalTechniques(Array.from(new Set(finalArray)).sort((a, b) => a.localeCompare(b)))
    setAllSelected({
      tactics: Array.from(new Set(allSelectedTactics)),
      techniques: Array.from(new Set(allSelectedTechniques)),
    })

    setPartiallySelected({
      tactics: Array.from(new Set(partiallySelectedTactics)),
      techniques: Array.from(new Set(partiallySelectedTechniques)),
    })
  }, [selectedSubTechniques, selectedTechniques])

  useEffect(() => {
    if (triggerSubmit) {
      setTriggerSubmit(false)
      handleSubmit()
    }
  }, [triggerSubmit])

  useEffect(() => {
    if (!isOpen) {
      setIsFirstLoad(true)
      return
    }

    const fetchTechniques = async () => {
      try {
        const mitreStat = await statService.getMitreStat()
        const statIds = Object.keys(mitreStat.count).map((id) => id.toUpperCase())
        const mitreParsed = parseMitreData(assets.mitreAttck, statIds)

        setAllMitreData(mitreParsed)
        setFilteredMitreData(mitreParsed)
      } catch (e) {
        logger.error(e)
        setHasError(true)
      }
    }

    fetchTechniques()
  }, [isOpen, assets.mitreAttck])

  useEffect(() => {
    if (!isFirstLoad || !(isOpen && filteredMitreData.length)) return

    setIsFirstLoad(false)
    setSelectionFromInput(searchTechniques)
  }, [searchTechniques, isOpen, filteredMitreData]) // Triggered if techniques removed from threat hunting's bottom filter summary

  const MobileContent = () => (
    <div className="mitre-mobile-body">
      <div className="mitre-list-header">
        <Checkbox
          className="mitre-header-column"
          labelClassName="text-m text-upper"
          label={`${_t('tactic')}`}
          onChange={handleSelectAllTactics}
          checked={
            selectedFinalTechniques.length > 0 &&
            (allMitreData.some(
              (tactic) =>
                allSelected.tactics.includes(tactic.tactic) || partiallySelected.tactics.includes(tactic.tactic)
            ) ||
              allSelected.tactics.length === allMitreData.length)
          }
          partialSelection={allSelected.tactics.length !== allMitreData.length}
        />
      </div>
      <div className="mitre-list-wrapper">
        <div className="mitre-items-list">
          {filteredMitreData?.map((tactic) => {
            const isOpenAccordion = openTactic?.tactic === tactic.tactic

            return (
              <div key={tactic.tactic} className="d-block">
                <button
                  type="button"
                  className={`w-100 mitre-tactic-accordion ${isOpenAccordion ? 'selected' : ''}`}
                  onClick={() => handleOpenTactic(tactic)}
                >
                  <div className="d-flex justify-content-start flex-grow-1">
                    <span className="me-3 text-muted text-m">{tactic.tactic}</span>
                    <span className="text-muted">{tactic.name}</span>
                  </div>
                  <div className="d-flex justify-content-end pe-2">
                    <div className="number-box text-m text-label text-muted">{tactic.techniques.length}</div>
                    <i className={`ds3-icon ${isOpenAccordion ? 'ds3-solid-down' : 'ds3-solid-right'}`} />
                  </div>
                </button>
                <div className={`${openTactic ? 'mitre-items-list' : 'd-none'}`}>
                  {!!openTactic &&
                    openTactic.tactic === tactic.tactic &&
                    openTactic.techniques.map((technique, idx) => {
                      const isOpenAccordion = openTechnique?.id === technique.id
                      const overflows = idx >= filteredMitreData.length

                      return (
                        <div key={technique.id} className="d-block">
                          <MobileTechniqueAccordion
                            isOpenAccordion={isOpenAccordion}
                            overflows={overflows}
                            handleOpenTechnic={handleOpenTechnic}
                            technique={technique}
                            handleSelectTechnique={handleSelectTechnique}
                            partiallySelected={partiallySelected}
                            selectedFinalTechniques={selectedFinalTechniques}
                          />
                          <div className={`${openTechnique ? 'mitre-items-list' : 'd-none'}`}>
                            <MobileOpenSubTechnique
                              technique={technique}
                              openTactic={openTactic}
                              openTechnique={openTechnique}
                              handleSelectSubtechnique={handleSelectSubtechnique}
                              selectedSubTechniques={selectedSubTechniques}
                            />
                          </div>
                        </div>
                      )
                    })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )

  const DesktopContent = () => (
    <div className="mitre-search-body">
      <div className="mitre-list-header">
        <Checkbox
          className="mitre-header-column"
          labelClassName="text-m text-upper"
          label={`${_t('tactic')}`}
          onChange={handleSelectAllTactics}
          checked={
            selectedFinalTechniques.length > 0 &&
            (allMitreData.some(
              (tactic) =>
                allSelected.tactics.includes(tactic.tactic) || partiallySelected.tactics.includes(tactic.tactic)
            ) ||
              allSelected.tactics.length === allMitreData.length)
          }
          partialSelection={allSelected.tactics.length !== allMitreData.length}
        />
        <Checkbox
          className="mitre-header-column"
          labelClassName="text-m text-upper"
          label={`${_t('technique')}`}
          onChange={() => handleSelectTactic(openTactic)}
          checked={
            !!openTactic &&
            (partiallySelected.tactics.includes(openTactic.tactic) || allSelected.tactics.includes(openTactic.tactic))
          }
          partialSelection={!!openTactic && partiallySelected.tactics.includes(openTactic.tactic)}
        />
        <Checkbox
          className="mitre-header-column"
          labelClassName="text-m text-upper"
          label={`${_t('subtechnique')}`}
          onChange={() => handleSelectTechnique(openTechnique)}
          checked={
            !!openTechnique &&
            (allSelected.techniques.includes(openTechnique.id) ||
              partiallySelected.techniques.includes(openTechnique.id))
          }
          partialSelection={!!openTechnique && partiallySelected.techniques.includes(openTechnique.id)}
        />
      </div>
      <div className="mitre-list-wrapper">
        <div className="mitre-items-list">
          {filteredMitreData?.map((tactic) => {
            const isOpenAccordion = openTactic?.tactic === tactic.tactic

            return (
              <button
                type="button"
                key={tactic.tactic}
                className={`w-100 mitre-tactic-accordion ${isOpenAccordion ? 'selected' : ''}`}
                onClick={() => handleOpenTactic(tactic)}
              >
                <div className="d-flex justify-content-start flex-grow-1">
                  <Checkbox
                    className="me-3"
                    labelClassName="text-muted text-m"
                    label={tactic.tactic}
                    onChange={() => handleSelectTactic(tactic)}
                    checked={
                      allSelected.tactics.includes(tactic.tactic) || partiallySelected.tactics.includes(tactic.tactic)
                    }
                    partialSelection={partiallySelected.tactics.includes(tactic.tactic)}
                  />
                  <span className="text-muted">{tactic.name}</span>
                </div>
                <div className="d-flex justify-content-end pe-2">
                  <div className="number-box text-m text-label text-muted">{tactic.techniques.length}</div>
                  <i className={`ds3-icon ${isOpenAccordion ? 'ds3-solid-left' : 'ds3-solid-right'}`} />
                </div>
              </button>
            )
          })}
        </div>
        <div className={`${openTactic ? 'mitre-items-list' : 'd-none'}`}>
          {!!openTactic &&
            openTactic.techniques.map((technique, idx) => {
              const isOpenAccordion = openTechnique?.id === technique.id
              const hasSubtechniques = (technique?.subtechniques?.length ?? 0) > 0
              const overflows = idx >= filteredMitreData.length

              return (
                <button
                  type="button"
                  key={technique.id}
                  className={`w-100 mitre-technique-accordion ${isOpenAccordion ? 'selected' : ''} ${
                    overflows ? 'overflows' : ''
                  }`}
                  onClick={() => (hasSubtechniques ? handleOpenTechnic(technique) : {})}
                >
                  <div className="d-flex justify-content-start flex-grow-1">
                    <Checkbox
                      className="me-3"
                      labelClassName="text-muted text-m"
                      label={technique.id}
                      onChange={() => {
                        handleSelectTechnique(technique)
                      }}
                      checked={
                        partiallySelected.techniques.includes(technique.id) ||
                        selectedFinalTechniques.includes(technique.id)
                      }
                      partialSelection={partiallySelected.techniques.includes(technique.id)}
                    />
                    <span className="text-muted">{technique.name}</span>
                  </div>
                  <div className="d-flex justify-content-end pe-2">
                    {hasSubtechniques ? (
                      <div className="d-flex">
                        <div className="number-box text-m text-label text-muted">
                          {technique?.subtechniques?.length}
                        </div>
                        <i className={`ds3-icon ${isOpenAccordion ? 'ds3-solid-left' : 'ds3-solid-right'}`} />
                      </div>
                    ) : null}
                  </div>
                </button>
              )
            })}
        </div>
        <div className={`${openTechnique ? 'mitre-items-list' : 'd-none'}`}>
          {openTechnique?.subtechniques?.length &&
            openTechnique.subtechniques.map((technique, idx) => (
              <div
                key={technique.id}
                className={`mitre-subtechnique-line ${idx >= (openTactic?.techniques?.length ?? 0) ? 'overflows' : ''}`}
              >
                <Checkbox
                  labelClassName="text-muted text-m"
                  className="me-3"
                  label={technique.id.split('.')[1]}
                  onChange={() => {
                    handleSelectSubtechnique(technique)
                  }}
                  checked={selectedSubTechniques.some((sub) => sub.id === technique.id)}
                />
                <span className="text-muted">{technique.name}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )

  const MitreSearchContent = () =>
    isOpen ? (
      <div className="mitre-search-container">
        <div className="mitre-filter">
          <div className="w-100 d-block flex-grow-1">
            <p className="pb-1 m-0">{_t('search')}</p>
            <TextInput
              id="mitre-search"
              placeholder={_t('search-mitre-id')}
              value={searchInput}
              onChange={handleSearchChange}
              prefix={<i className="ds3-icon ds3-search" />}
              //postfix={<i className="ds3 ds3-help" />} // Disabled helper until having helper text
              clearable
            />
          </div>
        </div>
        <MobileContent />
        <DesktopContent />
      </div>
    ) : (
      <></>
    )

  return (
    <>
      <p className="text-note text-m m-0 pb-1">MITRE ATT&CK</p>
      <button type="button" className="unstyled-button w-100" onClick={() => setOpen(true)}>
        <TextInput
          inputClassName="tags-filter-input"
          id={filterId}
          placeholder={`${_t('browse')} ${_t('mitre-techniques')}`}
          value={
            filterValues.length ? (
              <FilterValues values={filterValues} showAll={false} onRemove={handleRemoveFromInput} />
            ) : (
              ''
            )
          }
          postfix={<i className="ds3-icon ds3-external-link ds3-hover" />}
          cursor="pointer"
          noWrap
          readOnly
        />
      </button>
      {isOpen ? (
        <FullPageDialog
          body={
            hasError ? <span>{_t('There was an error during fetching MITRE Techniques')}</span> : MitreSearchContent()
          }
          title="MITRE ATT&CK"
          description=""
          isOpen
          onClose={() => setOpen(false)}
          filteredList={selectedFinalTechniques}
          //contentLength={getTacticsNumber(allMitreData)} // Unused for now
          onClearSelection={handleClearSelection}
          onRemoveFilter={handleRemoveTechnique}
          onApply={handleSubmit}
          enableSubmit={enableSubmit}
          filterSummaryLabel={`${_t('mitre-techniques-selected')}:`}
          noFilterLabel={_t('no-mitre-techniques-selected')}
          applyButtonLabel={_t('apply')}
        />
      ) : null}
    </>
  )
}

type MobileTechniqueAccordionProps = {
  isOpenAccordion: boolean
  overflows: boolean
  handleOpenTechnic: (technique: TechniqueType) => void
  technique: TechniqueType
  handleSelectTechnique: (technique?: TechniqueType | undefined) => void
  partiallySelected: {
    tactics: string[]
    techniques: string[]
  }
  selectedFinalTechniques: string[]
}

const MobileTechniqueAccordion = ({
  isOpenAccordion,
  overflows,
  handleOpenTechnic,
  technique,
  handleSelectTechnique,
  partiallySelected,
  selectedFinalTechniques,
}: MobileTechniqueAccordionProps) => {
  const hasSubtechniques = (technique?.subtechniques?.length ?? 0) > 0

  return (
    <button
      className={`mitre-technique-accordion text-start w-100 ${isOpenAccordion ? 'selected' : ''} ${
        overflows ? 'overflows' : ''
      }`}
      onClick={() => (hasSubtechniques ? handleOpenTechnic(technique) : {})}
    >
      <div className="d-flex justify-content-start flex-grow-1">
        <Checkbox
          className="me-3"
          labelClassName="text-muted text-m"
          label={technique.id}
          onChange={() => {
            handleSelectTechnique(technique)
          }}
          checked={
            partiallySelected.techniques.includes(technique.id) || selectedFinalTechniques.includes(technique.id)
          }
          partialSelection={partiallySelected.techniques.includes(technique.id)}
        />
        <span className="text-muted">{technique.name}</span>
      </div>
      <div className="d-flex justify-content-end pe-2">
        {hasSubtechniques ? (
          <div className="d-flex">
            <div className="number-box text-m text-label text-muted">{technique?.subtechniques?.length}</div>
            <i className={`ds3-icon ${isOpenAccordion ? 'ds3-solid-down' : 'ds3-solid-right'}`} />
          </div>
        ) : null}
      </div>
    </button>
  )
}

type MobileOpenSubTechniqueProps = {
  technique: TechniqueType
  openTactic: TacticType
  handleSelectSubtechnique: (subtechnique?: TechniqueType | undefined) => void
  selectedSubTechniques: TechniqueType[]
  openTechnique?: TechniqueType
}

const MobileOpenSubTechnique = ({
  technique,
  openTactic,
  openTechnique,
  handleSelectSubtechnique,
  selectedSubTechniques,
}: MobileOpenSubTechniqueProps) => {
  return (
    openTechnique?.subtechniques?.length &&
    openTechnique.id === technique.id &&
    openTechnique.subtechniques.map((sub, idx) => (
      <div
        key={sub.id}
        className={`mitre-subtechnique-line ${idx >= (openTactic?.techniques?.length ?? 0) ? 'overflows' : ''}`}
      >
        <Checkbox
          labelClassName="text-muted text-m"
          className="me-3"
          label={sub.id.split('.')[1]}
          onChange={() => {
            handleSelectSubtechnique(sub)
          }}
          checked={selectedSubTechniques.some((item) => item.id === sub.id)}
        />
        <span className="text-muted">{sub.name}</span>
      </div>
    ))
  )
}

const parseMitreData = (mitreAssets: MitreData | undefined, mitreStatIds: string[]): TacticType[] => {
  if (!mitreAssets) {
    return []
  }

  return Object.entries(mitreAssets)
    .map(([tacticId, tactic]) => {
      const techniques: TechniqueType[] = []
      for (const [techniqueId, technique] of Object.entries(tactic.techniques)) {
        if (!mitreStatIds.includes(techniqueId)) {
          continue
        }

        const [baseId, subId] = techniqueId.split('.')
        const techniqueObj = { id: techniqueId, name: technique.name }

        if (subId) {
          const parentIndex = techniques.findIndex((t) => t.id === baseId)
          if (parentIndex !== -1) {
            techniques[parentIndex].subtechniques ??= []
            techniques[parentIndex]?.subtechniques?.push({ ...techniqueObj, parentId: baseId })
          }
        } else {
          techniques.push({ ...techniqueObj, parentId: tacticId, subtechniques: [] })
        }
      }

      techniques.sort((a, b) => a.id.localeCompare(b.id))
      techniques.forEach((t) => t.subtechniques?.sort((a, b) => a.id.localeCompare(b.id)))

      return { tactic: tacticId, name: tactic.name, techniques }
    })
    .filter((tactic) => tactic.techniques.length > 0)
    .sort((a, b) => a.tactic.localeCompare(b.tactic))
}

function getFilteredMitreData(searchInput: string, allMitreData: TacticType[]) {
  const searchInputLower = searchInput.toLowerCase()

  return allMitreData
    .map((tactic) => ({
      ...tactic,
      techniques: tactic.techniques
        .filter((technique) => {
          const techniqueId = technique.id.toString().toLowerCase()
          const techniqueName = technique.name.toLowerCase()
          const subtechniques = technique.subtechniques ?? []

          return (
            techniqueId.includes(searchInputLower) ||
            techniqueName.includes(searchInputLower) ||
            subtechniques.some((sub) => {
              // Search in subtechniques as well, if there is a match, we show the technique
              const subId = sub.id.toString().toLowerCase()
              const subName = sub.name.toLowerCase()

              return subId.includes(searchInputLower) || subName.includes(searchInputLower)
            })
          )
        })
        .map((technique) => ({
          ...technique,
          // Filter out non-matching subtechniques
          subtechniques: technique?.subtechniques?.filter((technique) => {
            const techniqueId = technique.id.toString().toLowerCase()
            const techniqueName = technique.name.toLowerCase()

            return techniqueId.includes(searchInputLower) || techniqueName.includes(searchInputLower)
          }),
        })),
    }))
    .filter((tactic) => tactic.techniques.length > 0)
}

export default MitreSearch
